import React, { useCallback, useEffect, useState } from "react";
import {
  Alert, Box, Chip, CircularProgress, Divider, Typography, Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as ReTooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { phappsRequest } from "@/services/apiService";
import { buildHaloHireChemistPath } from "@/config/apiConstants";
import { getAdminApiErrorMessage } from "@/utils/adminApiErrors";
import { pharmacyHaloDataGridSx } from "@/utils/adminTableStyles";

const HIRE = "#be123c";
const HIRE_LIGHT = "#fff1f2";

const money = (n) =>
  `$${Number(n || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (iso) => {
  if (!iso) return "—";
  const p = String(iso).split("T")[0].split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0].slice(2)}` : iso;
};

const ITEM_STATUS_COLORS = {
  available: { bg: "#dcfce7", fg: "#166534" },
  rented:    { bg: "#fef9c3", fg: "#854d0e" },
  dirty:     { bg: "#fee2e2", fg: "#991b1b" },
  broken:    { bg: "#fee2e2", fg: "#991b1b" },
  in_repair: { bg: "#e0e7ff", fg: "#3730a3" },
  retired:   { bg: "#f1f5f9", fg: "#475569" },
};

const HIRE_STATUS_COLORS = {
  active:      { bg: "#dbeafe", fg: "#1e40af" },
  returned:    { bg: "#dcfce7", fg: "#166534" },
  overdue:     { bg: "#fee2e2", fg: "#991b1b" },
  written_off: { bg: "#f1f5f9", fg: "#475569" },
};

function StatCard({ label, value, tone }) {
  return (
    <Box sx={{
      flex: "1 1 160px", minWidth: 160, p: 2, borderRadius: 2,
      border: "1px solid #e2e8f0", background: tone || "#fff",
    }}>
      <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>{label}</Typography>
      <Typography sx={{ fontSize: "1.6rem", fontWeight: 800, color: "#0f172a", lineHeight: 1.2 }}>
        {value}
      </Typography>
    </Box>
  );
}

function StatusChip({ value, map }) {
  const c = map[value] || { bg: "#f1f5f9", fg: "#475569" };
  return (
    <Chip
      label={String(value || "—").replace(/_/g, " ")}
      size="small"
      sx={{ background: c.bg, color: c.fg, fontWeight: 700, textTransform: "capitalize" }}
    />
  );
}

const ChemistHaloHireTab = ({ chemistID }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [perDay, setPerDay] = useState([]);
  const [hirings, setHirings] = useState([]);

  const load = useCallback(async () => {
    if (!chemistID) return;
    setLoading(true);
    setError(null);
    try {
      const [s, c, p, r] = await Promise.all([
        phappsRequest(buildHaloHireChemistPath(chemistID, "Summary"), "GET"),
        phappsRequest(buildHaloHireChemistPath(chemistID, "Catalog"), "GET"),
        phappsRequest(buildHaloHireChemistPath(chemistID, "PerDay"), "GET", null, { days: 30 }),
        phappsRequest(buildHaloHireChemistPath(chemistID, "RecentHirings"), "GET", null, { limit: 50 }),
      ]);
      setSummary(s?.data ?? null);
      setCatalog(c?.data ?? []);
      setPerDay(p?.data ?? []);
      setHirings(r?.data ?? []);
    } catch (err) {
      setError(getAdminApiErrorMessage(err) || "Failed to load HaloHire data.");
    } finally {
      setLoading(false);
    }
  }, [chemistID]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
      <CircularProgress sx={{ color: HIRE }} />
    </Box>;
  }
  if (error) return <Alert severity="error">{error}</Alert>;

  const columns = [
    { field: "date", headerName: "Date", width: 110, valueFormatter: (v) => fmtDate(v) },
    {
      field: "status", headerName: "Status", width: 130, sortable: true,
      renderCell: (params) => <StatusChip value={params.value} map={HIRE_STATUS_COLORS} />,
    },
    { field: "patient", headerName: "Patient", flex: 1, minWidth: 150 },
    { field: "item", headerName: "Item hired", flex: 1.4, minWidth: 200 },
    {
      field: "durationDays", headerName: "Duration", width: 110,
      valueFormatter: (v) => (v == null ? "—" : `${v} day${v === 1 ? "" : "s"}`),
    },
    {
      field: "revenue", headerName: "Revenue", width: 120, align: "right", headerAlign: "right",
      valueFormatter: (v) => money(v),
    },
    {
      field: "dueDate", headerName: "Due date", width: 150,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <span>{fmtDate(params.value)}</span>
          {params.row.isOverdue && (
            <Chip label={`${params.row.daysOverdue}d late`} size="small"
              sx={{ background: "#fee2e2", color: "#991b1b", fontWeight: 700, height: 20 }} />
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <StatCard label="Total hirings" value={summary?.totalHirings ?? 0} />
        <StatCard label="Total revenue" value={money(summary?.totalRevenue)} />
        <StatCard label="Items added" value={summary?.itemsAdded ?? 0} />
        <StatCard
          label="Overdue items"
          value={summary?.overdueItems ?? 0}
          tone={summary?.overdueItems > 0 ? HIRE_LIGHT : undefined}
        />
      </Box>

      <Box>
        <Typography fontWeight={700} mb={1}>Hirings per day (30d)</Typography>
        <Box sx={{ height: 220, border: "1px solid #e2e8f0", borderRadius: 2, p: 1.5 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={perDay}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="date" tickFormatter={fmtDate} fontSize={11} tickLine={false} />
              <YAxis allowDecimals={false} fontSize={11} tickLine={false} width={30} />
              <ReTooltip labelFormatter={fmtDate} formatter={(v) => [v, "Hirings"]} />
              <Bar dataKey="count" fill={HIRE} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>

      <Box>
        <Typography fontWeight={700} mb={1}>Categories &amp; items</Typography>
        {catalog.length === 0 ? (
          <Alert severity="info">No hire categories set up yet.</Alert>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {catalog.map((cat) => (
              <Box key={cat.categoryId}
                sx={{ border: "1px solid #e2e8f0", borderRadius: 2, overflow: "hidden" }}>
                <Box sx={{
                  px: 2, py: 1, background: HIRE_LIGHT,
                  display: "flex", alignItems: "center", gap: 1,
                }}>
                  <Typography fontWeight={700} sx={{ color: HIRE }}>{cat.categoryName}</Typography>
                  {!cat.isActive && <Chip label="inactive" size="small" />}
                  <Typography variant="caption" sx={{ color: "#64748b", ml: "auto" }}>
                    {cat.itemTypes.reduce((n, t) => n + t.items.length, 0)} item(s)
                  </Typography>
                </Box>
                <Box sx={{ p: 1.5 }}>
                  {cat.itemTypes.length === 0 && (
                    <Typography variant="body2" color="text.secondary">No item types yet.</Typography>
                  )}
                  {cat.itemTypes.map((t, i) => (
                    <Box key={t.itemTypeId}>
                      {i > 0 && <Divider sx={{ my: 1 }} />}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <Typography variant="body2" fontWeight={600}>{t.itemTypeName}</Typography>
                        <Tooltip title="Weekly rate / deposit">
                          <Typography variant="caption" sx={{ color: "#64748b" }}>
                            {money(t.rateWeekly)}/wk · {money(t.depositAmount)} dep
                          </Typography>
                        </Tooltip>
                      </Box>
                      <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mt: 0.75 }}>
                        {t.items.length === 0
                          ? <Typography variant="caption" color="text.secondary">No items</Typography>
                          : t.items.map((it) => (
                            <Tooltip key={it.itemId}
                              title={`${it.serialNumber ? `Serial ${it.serialNumber} · ` : ""}Condition: ${it.condition}`}>
                              <Chip
                                size="small"
                                label={`${it.label} · ${String(it.status).replace(/_/g, " ")}`}
                                sx={{
                                  background: (ITEM_STATUS_COLORS[it.status] || {}).bg || "#f1f5f9",
                                  color: (ITEM_STATUS_COLORS[it.status] || {}).fg || "#475569",
                                  fontWeight: 600,
                                }}
                              />
                            </Tooltip>
                          ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <Box>
        <Typography fontWeight={700} mb={1}>Recent hirings</Typography>
        <DataGrid
          rows={hirings}
          columns={columns}
          getRowId={(r) => r.hireId}
          density="compact"
          autoHeight
          disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          pageSizeOptions={[25, 50, 100]}
          sx={pharmacyHaloDataGridSx}
        />
      </Box>
    </Box>
  );
};

export default ChemistHaloHireTab;
